import { TemperatureDomainService } from './domain';
import { TemperatureFactory } from './entity/temperatureFactory';

import type { TemperatureIRepository } from './entity/temperatureIRepository';
import type { DeleteOutputType, EntitySubscribeType } from '@/infra/appsync';
import type { Logger } from '@/infra/logger';
import type { TemperatureJson, TemperatureJsonPvt } from '@/types/domain/temperature/entities/temperatureEntity';

/**
 * 体温アプリケーションサービス
 */
export class TemperatureApplicationService {
  private service!: TemperatureDomainService;

  private temperatureFactory = new TemperatureFactory();

  /**
   * Injection
   * @param temperatureRepository temperatureRepository
   * @param logger logger
   */
  public inject(
    temperatureRepository: TemperatureIRepository,
    logger: Logger,
  ) {
    this.service = new TemperatureDomainService(logger);
    this.service.inject(temperatureRepository);
  }

  /**
   * 体温リスト取得
   * @param token トークン
   * @returns リスト
   */
  async getTemperatureList(token: string | null) {
    const result = await this.service.getTemperatureList(token);
    return result;
  }

  /**
   * 指定日の体温リスト取得
   * @param date 日付
   * @returns リスト
   */
  async getTemperatureListWithDate(date: string) {
    const result = await this.service.getTemperatureListWithDate(date);
    return result;
  }

  /**
   * 体温作成
   * @param input 入力
   * @returns 結果
   */
  async createTemperature(
    input: TemperatureJson,
  ) {
    const entity = this.temperatureFactory.createFromJson(input);
    const result = await this.service.createTemperature(entity);
    return result;
  }

  /**
   * 体温更新
   * @param input 入力
   * @returns 結果
   */
  async updateTemperature(
    input: TemperatureJson,
  ) {
    const entity = this.temperatureFactory.createFromJson(input);
    const result = await this.service.updateTemperature(entity);
    return result;
  }

  /**
   * 体温削除
   * @param input 入力
   * @returns 結果
   */
  async deleteTemperature(
    input: TemperatureJson,
  ) {
    const entity = this.temperatureFactory.createFromJson(input);
    const result = await this.service.deleteTemperature(entity);
    return result;
  }

  /**
   * 体温リストを更新する(Create)
   * @param temperatureList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public createTemperatureStore(
    temperatureList: TemperatureJson[],
    payload: EntitySubscribeType,
    filter: ((json: TemperatureJsonPvt) => boolean) | undefined,
  ) {
    return this.service.createEntityStorePub(
      temperatureList,
      payload,
      this.temperatureFactory,
      filter,
    );
  }

  /**
   * 体温リストを更新する(Update)
   * @param temperatureList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public updateTemperatureStore(
    temperatureList: TemperatureJson[],
    payload: EntitySubscribeType,
  ) {
    return this.service.updateEntityStorePub(
      temperatureList,
      payload,
      this.temperatureFactory,
    );
  }

  /**
   * 体温リストを更新する(Delete)
   * @param temperatureList リスト
   * @param payload 取得値
   * @returns 更新リスト
   */
  public deleteTemperatureStore(
    temperatureList: TemperatureJson[],
    payload: DeleteOutputType,
  ) {
    return this.service.deleteEntityStorePub(
      temperatureList,
      payload,
      this.temperatureFactory,
    );
  }
}
